'use client';

import React, { useState } from 'react';
import { X, Cookie, BarChart2, Megaphone, Check, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface CookieSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CookieSettingsModal({ open, onClose }: CookieSettingsModalProps) {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  if (!open) return null;

  const save = () => {
    localStorage.setItem('pg-cookie-consent', JSON.stringify({
      necessary: true,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
    }));
    toast.success('Cookie-Einstellungen gespeichert');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-lg rounded-2xl p-6 animate-bounce-in"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Cookie size={20} style={{ color: 'var(--primary)' }} />
            <h2 className="font-bold text-lg">Cookie-Einstellungen</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Necessary */}
          <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'var(--muted)' }}>
            <Shield size={18} style={{ color: 'var(--neon-green)', marginTop: 2 }} />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-sm">Notwendige Cookies</p>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: 'rgba(74,222,128,0.15)', color: 'var(--neon-green)' }}>
                  Immer aktiv
                </span>
              </div>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                Erforderlich für den Betrieb der Website. Können nicht deaktiviert werden.
              </p>
            </div>
          </div>

          {/* Analytics */}
          <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'var(--muted)' }}>
            <BarChart2 size={18} style={{ color: 'var(--neon-cyan)', marginTop: 2 }} />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-sm">Analyse-Cookies</p>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200"
                  style={{ background: analytics ? 'var(--primary)' : 'var(--border)' }}
                  role="switch"
                  aria-checked={analytics}
                >
                  <span
                    className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform duration-200"
                    style={{ transform: analytics ? 'translateX(18px)' : 'translateX(2px)' }}
                  />
                </button>
              </div>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                Helfen uns zu verstehen, wie die Website genutzt wird (z.B. Google Analytics).
              </p>
            </div>
          </div>

          {/* Marketing */}
          <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'var(--muted)' }}>
            <Megaphone size={18} style={{ color: 'var(--neon-amber)', marginTop: 2 }} />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-sm">Marketing-Cookies</p>
                <button
                  onClick={() => setMarketing(!marketing)}
                  className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200"
                  style={{ background: marketing ? 'var(--primary)' : 'var(--border)' }}
                  role="switch"
                  aria-checked={marketing}
                >
                  <span
                    className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform duration-200"
                    style={{ transform: marketing ? 'translateX(18px)' : 'translateX(2px)' }}
                  />
                </button>
              </div>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                Werden für personalisierte Werbung und Retargeting verwendet.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:bg-white/5"
            style={{ border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
          >
            Abbrechen
          </button>
          <button
            onClick={save}
            className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5 text-sm"
          >
            <Check size={14} />
            Einstellungen speichern
          </button>
        </div>
      </div>
    </div>
  );
}